import { useContext, useEffect, useRef, useState } from "react";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { SuperviseesLeaveRequestColumns } from "../data";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { useGetLeaveRequest } from "../../../../services/leave-request.service";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import {
	LEAVE_REQUEST_OPTIONS,
	LeaveRequestDto,
} from "../../../../models/leave-request.dto";
import { useManageTableWithSearchParams } from "../../../../helpers/useManageTable";
import CustomStyledTable from "../../../../components/table/CustomStyledTable";
import { Popover } from "@mui/material";
import { LuEye } from "react-icons/lu";
import LeaveRequestDetailsModal from "../modals/LeaveRequestDetailsModal";
import { useSearchParams } from "react-router-dom";
import TableFilter from "../../../../components/table/filter/TableFilter";
import { EmployeeSupervisees } from "../../../../routes";
import { superviseesLeaveRequestColumns } from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { useTableConfiguration } from "../../../../helpers/useTableConfiguration";
import { TableId } from "../../../../models/table-configuration.dto";
import useUserStore from "../../../../state-management/useUserStore";

const SuperviseesLeaveRequestsView = () => {
	const [searchParams] = useSearchParams();
	const requestId = searchParams.get("id");
	const { setFormData } = useEditFormStore();
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openLeaveRequestDetailsModal, setOpenLeaveRequestDetailsModal] =
		useState(false);
	const supervisees = useContext(EmployeeSupervisees);
	const currentUser = useUserStore().currentUser.user;

	useEffect(() => {
		if (requestId) {
			setOpenLeaveRequestDetailsModal(true);
		}
	}, [requestId]);

	const { tableAPIRef, tableData, isLoading, pageCount, setTableData } =
		useManageTableWithSearchParams<LeaveRequestDto>({
			fetchHook: useGetLeaveRequest,
			options: { queryMode: "SUPERVISEES" },
		});

	const handleClose = () => setAnchorEl(null);

	const additionalColumns = [
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			minWidth: 150,
			flex: 1,
			headerClassName: "super-app-theme--header",
			cellClassName: "actions ",
			getActions: (params: any) => {
				return [
					<GridActionsCellItem
						icon={<BiDotsHorizontalRounded />}
						label="actions"
						onClick={(e: any) => {
							// console.log(params.row, "params");
							setFormData(params?.row);
							setAnchorEl(e.currentTarget);
						}}
					/>,
				];
			},
		},
	];

	const columnsWithAdditions = SuperviseesLeaveRequestColumns.map(
		(obj) => additionalColumns.find((o) => o.field === obj.field) || obj
	);

	return (
		<>
			<div className="flex justify-between mb-6">
				<TableFilter
					fields={[
						{
							title: "",
							fieldName: "",
							fieldType: "SORT",
							options: [
								{
									title: "Start Date",
									fieldName: "startDate",
								},
								{
									title: "End Date",
									fieldName: "returnDate",
								},
								{
									title: "Date Created",
									fieldName: "createdAt",
								},
								{
									title: "Date Modified",
									fieldName: "modifiedAt",
								},
							],
						},
						{
							title: "Supervisee",
							fieldName: "employeeId",
							fieldType: "SELECT_FIELD",
							options: supervisees || [],
							mapTexts: ["firstName", "lastName"],
						},
						{
							title: "Status",
							fieldName: "status",
							fieldType: "CHECKBOX_LIST",
							options: LEAVE_REQUEST_OPTIONS,
						},
						{
							title: "Start Date",
							fieldName: "startDate",
							fieldType: "DATE_RANGE",
						},
						{
							title: "End Date",
							fieldName: "returnDate",
							fieldType: "DATE_RANGE",
						},
					]}
				/>
			</div>
			<TanStackEmployeeTable
				tableId={TableId.SUPERVISEES_LEAVE_REQUESTS}
				data={tableData}
				columns={superviseesLeaveRequestColumns}
				loading={isLoading}
				onRowClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={{}}
				pageSize={10}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			{/* <CustomStyledTable
				rows={tableData}
				columns={columnsWithAdditions}
				apiRef={tableAPIRef}
				loading={isLoading}
				pageCount={pageCount}
				setAnchorEl={setAnchorEl}
			/> */}
			<Popover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				PaperProps={{
					style: {
						width: "auto",
						borderRadius: 8,
						display: "flex",
						justifyContent: "center",
					},
				}}
			>
				<div className="flex flex-col justify-center p-3 text-secondary-darkgray">
					<div
						onClick={() => {
							setOpenLeaveRequestDetailsModal(true);
							handleClose();
						}}
						className="table-action-popover"
					>
						<LuEye />
						<p className="text-2sm">View Details</p>
					</div>
				</div>
			</Popover>

			{openLeaveRequestDetailsModal && (
				<LeaveRequestDetailsModal
					open={openLeaveRequestDetailsModal}
					title={"Request Details"}
					setOpen={setOpenLeaveRequestDetailsModal}
					tableAPIRef={tableAPIRef}
					supervisor
				/>
			)}
		</>
	);
};

export default SuperviseesLeaveRequestsView;
